import mapboxgl from 'mapbox-gl';
import { toast } from '@/hooks/use-toast';
import { supabase } from "@/integrations/supabase/client";
import { debounce } from 'lodash';

// Memoize token fetching to prevent multiple simultaneous requests
const debouncedFetchToken = debounce(async () => {
  const { data, error } = await supabase.functions.invoke('get-mapbox-token');
  if (error) throw error;
  return data?.token;
}, 1000, { leading: true, trailing: false });

// Cache the token to prevent unnecessary API calls
let cachedToken: string | null = null;

// Cache map style loading state
let styleLoaded = false;

export const initializeMap = async (
  container: HTMLDivElement,
  center: [number, number]
): Promise<mapboxgl.Map | null> => {
  try {
    console.log('Initializing map with center:', center);
    
    // Use cached token if available
    if (!cachedToken) {
      cachedToken = await debouncedFetchToken();
    }
    
    if (!cachedToken) {
      console.error('No token received from edge function');
      toast({
        title: "Error loading map",
        description: "Map configuration error",
        variant: "destructive",
      });
      return null;
    }

    console.log('Successfully received Mapbox token');
    mapboxgl.accessToken = cachedToken;
    
    // Validate container
    if (!container) {
      console.error('Map container is null');
      throw new Error('Map container is required');
    }

    console.log('Creating new map instance');
    const map = new mapboxgl.Map({
      container,
      style: 'mapbox://styles/mapbox/light-v11',
      center,
      zoom: 12,
      failIfMajorPerformanceCaveat: true,
      preserveDrawingBuffer: true,
      attributionControl: true,
      maxZoom: 18,
      minZoom: 9,
      bounds: [
        [-74.259090, 40.477399],
        [-73.700272, 40.916178]
      ],
      fitBoundsOptions: {
        padding: 50
      },
      // Add performance optimizations
      maxTileCacheSize: 50, // Limit tile cache to reduce memory usage
      localIdeographFontFamily: "'Noto Sans', sans-serif", // Use local fonts when possible
      trackResize: false // Disable automatic resize handling
    });

    // Add navigation control with reduced options for simplicity
    console.log('Adding navigation controls');
    map.addControl(
      new mapboxgl.NavigationControl({
        showCompass: false,
        visualizePitch: false
      }), 
      'top-right'
    );

    // Add scale control
    map.addControl(new mapboxgl.ScaleControl(), 'bottom-right');

    // Only load style once
    if (!styleLoaded) {
      await new Promise<void>((resolve, reject) => {
        const loadTimeout = setTimeout(() => {
          reject(new Error('Map style load timeout'));
        }, 10000);

        map.once('style.load', () => {
          clearTimeout(loadTimeout);
          styleLoaded = true;
          resolve();
        });

        map.once('error', (e) => {
          clearTimeout(loadTimeout);
          reject(e.error);
        });
      });
    }

    return map;
  } catch (error) {
    console.error('Error initializing map:', error);
    toast({
      title: "Error loading map",
      description: "Please try again later",
      variant: "destructive",
    });
    return null;
  }
};